import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { MemberModel, StudentExamModel } from '../../../../models';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';

interface StudentExamReportProps {
    member: MemberModel;
}

const StudentExamReport: React.FC<StudentExamReportProps> = ({ member }) => {
    const [exams, setExams] = useState<StudentExamModel[]>([]);
    const [selectedExamType, setSelectedExamType] = useState<string>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const q = query(
            collection(db, 'studentExams'),
            where('memberId', '==', member.id)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const list: StudentExamModel[] = [];
            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as StudentExamModel));
            // Sort by date ascending for chart
            list.sort((a, b) => a.date - b.date);
            setExams(list);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching exams:", err);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [member.id]);

    // Get unique exam types
    const examTypes = ['all', ...Array.from(new Set(exams.filter(e => e.examType).map(e => e.examType!)))];

    // Filter exams by selected type
    // Filter exams by selected type and date range
    const filteredExams = exams.filter(e => {
        const matchesType = selectedExamType === 'all' || e.examType === selectedExamType;
        const examDate = new Date(e.date);
        const matchesStart = startDate ? examDate >= new Date(startDate) : true;
        const matchesEnd = endDate ? examDate <= new Date(endDate) : true;
        return matchesType && matchesStart && matchesEnd;
    });

    // Get all unique subjects
    const allSubjects = Array.from(
        new Set(filteredExams.flatMap(exam => Object.keys(exam.scores)))
    );

    // Prepare chart data
    const chartData = filteredExams.map(exam => {
        const dataPoint: any = {
            date: new Date(exam.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }),
            examTitle: exam.title,
        };

        Object.entries(exam.scores).forEach(([subject, score]) => {
            dataPoint[subject] = score;
        });

        return dataPoint;
    });

    // Color palette for lines
    const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                <p className="text-slate-400 font-bold">데이터를 불러오는 중입니다...</p>
            </div>
        );
    }

    if (filteredExams.length === 0) {
        return (
            <div className="py-20 text-center bg-white rounded-2xl border border-slate-100 shadow-sm">
                <TrendingUp size={48} className="mx-auto text-slate-100 mb-4" />
                <h3 className="text-lg font-black text-slate-800">분석할 데이터가 없습니다</h3>
                <p className="text-sm font-medium text-slate-400 mt-1">시험 성적을 등록하면 추이를 확인할 수 있습니다.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-500 shrink-0">시험 구분:</span>
                    <div className="flex items-center gap-1 flex-wrap">
                        {examTypes.map(type => (
                            <button
                                key={type}
                                onClick={() => setSelectedExamType(type)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${selectedExamType === type
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                                    }`}
                            >
                                {type === 'all' ? '전체' : type}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="w-px h-8 bg-slate-100 hidden sm:block" />

                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-slate-500 shrink-0">기간:</span>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="px-3 py-1.5 bg-slate-50 border-none rounded-lg text-xs font-bold text-slate-700 outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all"
                    />
                    <span className="text-slate-300">-</span>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="px-3 py-1.5 bg-slate-50 border-none rounded-lg text-xs font-bold text-slate-700 outline-none ring-1 ring-transparent focus:ring-indigo-100 transition-all"
                    />
                </div>
            </div>

            {/* Chart */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <h3 className="text-lg font-black text-slate-800 mb-4">과목별 성적 추이</h3>
                <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                            dataKey="date"
                            stroke="#94a3b8"
                            style={{ fontSize: '12px', fontWeight: 600 }}
                        />
                        <YAxis
                            domain={[0, 100]}
                            stroke="#94a3b8"
                            style={{ fontSize: '12px', fontWeight: 600 }}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 600
                            }}
                        />
                        <Legend
                            wrapperStyle={{ fontSize: '12px', fontWeight: 600 }}
                        />
                        {allSubjects.map((subject, index) => (
                            <Line
                                key={subject}
                                type="linear"
                                dataKey={subject}
                                stroke={colors[index % colors.length]}
                                strokeWidth={2}
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                connectNulls={true}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {allSubjects.map((subject, index) => {
                    const scores = filteredExams
                        .map(e => e.scores[subject])
                        .filter(s => s !== undefined) as number[];

                    const avg = scores.length > 0
                        ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
                        : 0;

                    const max = scores.length > 0 ? Math.max(...scores) : 0;

                    return (
                        <div key={subject} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                                <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: colors[index % colors.length] }}
                                />
                                <span className="text-sm font-bold text-slate-700">{subject}</span>
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-xs">
                                    <span className="text-slate-400 font-medium">평균</span>
                                    <span className="font-black text-slate-700">{avg}점</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                    <span className="text-slate-400 font-medium">최고</span>
                                    <span className="font-black text-indigo-600">{max}점</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default StudentExamReport;
